import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';

const CaseClosureRateByOrganization = ({ title = 'Case closure rate by organization' }) => {
    const [loading, setLoading] = useState(true);
    const [organizations, setOrganizations] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const res = await DashboardServices.getCaseClosureRateByOrganization();
                const payload = res?.data ?? res;
                const dataArray = payload?.view?.data ?? payload?.data ?? [];

                const formatted = Array.isArray(dataArray) ? dataArray : [];

                if (mounted) {
                    setOrganizations(formatted);
                }
            } catch (err) {
                console.error('Failed to load case closure rate by organization', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <WidgetCard sx={{ minHeight: 200 }}>
                <CenteredSpinner />
            </WidgetCard>
        );
    }

    if (organizations.length === 0) {
        return (
            <WidgetCard sx={{ minHeight: 200 }}>
                <MainCardTitle>{title}</MainCardTitle>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            </WidgetCard>
        );
    }

    return (
        <WidgetCard sx={{ minHeight: 200 }}>
            <MainCardTitle>{title}</MainCardTitle>
            <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 3, 
                mt: 3,
                pb: 2,
                justifyContent: 'center'
            }}>
                {organizations.map((org, index) => (
                    <Box
                        key={org.organization_id || index}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minWidth: 140
                        }}
                    >
                        {/* Circular Progress */}
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'inline-flex',
                                mb: 1.5
                            }}
                        >
                            {/* Background circle (unresolved) */}
                            <CircularProgress
                                variant="determinate"
                                value={100}
                                size={100}
                                thickness={5}
                                sx={{
                                    color: '#f44336',
                                    position: 'absolute'
                                }}
                            />
                            {/* Foreground circle (closed) */}
                            <CircularProgress
                                variant="determinate"
                                value={org.closure_percentage || 0}
                                size={100}
                                thickness={5}
                                sx={{
                                    color: '#4caf50',
                                    position: 'relative',
                                    zIndex: 1
                                }}
                            />
                            {/* Percentage text */}
                            <Box
                                sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    component="div"
                                    sx={{ 
                                        color: 'text.primary',
                                        fontWeight: 700,
                                        fontSize: 24
                                    }}
                                >
                                    {org.closure_percentage || 0}%
                                </Typography>
                            </Box>
                        </Box>

                        {/* Organization name */}
                        <Typography 
                            sx={{ 
                                color: 'text.primary',
                                fontSize: 14,
                                fontWeight: 600,
                                textAlign: 'center'
                            }}
                        >
                            {org.organization_short_name || org.organization_name}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </WidgetCard>
    );
};

export default CaseClosureRateByOrganization;
